/**
 * Product Info Modal Component
 * Displays detailed product information in a modal
 *
 * @package
 */

import { useState, useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import { decodeHtmlEntities } from '../helpers/decodeHtmlEntities';
import { useFocusTrap } from '../helpers/useFocusTrap';
import { getImageAltText } from '../helpers/getImageAltText';
import { cachedApiFetch } from '../helpers/useApiCache';
import { formatPrice } from '../helpers/priceHelpers';
import Icon from './Icon';

const ProductInfoModal = ( { productId, isOpen, onClose, isSelected = false } ) => {
	const [ productData, setProductData ] = useState( null );
	const [ loading, setLoading ] = useState( false );
	const [ selectedImageIndex, setSelectedImageIndex ] = useState( 0 );
	const [ galleryImages, setGalleryImages ] = useState( [] );
	const modalRef = useRef( null );

	useFocusTrap( isOpen, modalRef );

	useEffect( () => {
		if ( ! isOpen ) {
			return;
		}

		const handleEscape = e => {
			if ( e.key === 'Escape' ) {
				onClose();
			}
		};

		document.addEventListener( 'keydown', handleEscape );

		return () => {
			document.removeEventListener( 'keydown', handleEscape );
		};
	}, [ isOpen, onClose ] );

	useEffect( () => {
		if ( ! isOpen || ! productId ) {
			return;
		}

		setLoading( true );
		cachedApiFetch( `/wp/v2/qcom_product/${ productId }?_embed` )
			.then( data => {
				setProductData( data );

				const images = [];
				const featuredMedia = data._embedded?.[ 'wp:featuredmedia' ]?.[ 0 ];
				if ( featuredMedia ) {
					// The batch payload's source_url is the card-sized 'medium'; the modal
					// shows the image big, so prefer the 'large' size when available.
					images.push( featuredMedia.media_details?.sizes?.large?.source_url || featuredMedia.source_url );
				}

				if ( data.acf?.gallery && Array.isArray( data.acf.gallery ) ) {
					data.acf.gallery.forEach( imageId => {
						cachedApiFetch( `/wp/v2/media/${ imageId }` )
							.then( media => {
								if ( media?.source_url ) {
									setGalleryImages( prev => [ ...prev, media.source_url ] );
								}
							} )
							.catch( () => {} );
					} );
				}

				if ( images.length > 0 ) {
					setGalleryImages( images );
				}
			} )
			.catch( () => {} )
			.finally( () => {
				setLoading( false );
			} );
	}, [ isOpen, productId ] );

	if ( ! isOpen ) {
		return null;
	}

	const handleOverlayClick = e => {
		if ( e.target === e.currentTarget ) {
			onClose();
		}
	};

	const getPrice = () => {
		if ( ! productData ) {
			return null;
		}
		// TODO: Remove meta fields and use REST API fields only. QuickCommerce is playing silly buggers with the price field.
		const price = productData.price ||
			productData.acf?.price ||
			productData.meta?._qcom_price?.[ 0 ] ||
			productData.meta?.price?.[ 0 ];

		if ( price !== null && price !== undefined && parseFloat( price ) > 0 ) {
			return formatPrice( parseFloat( price ) );
		}

		return null;
	};

	const getStatusText = () => {
		if ( isSelected ) {
			return __( 'Included in configuration', 'at-vehicle-configurator' );
		}

		const price = getPrice();
		if ( price ) {
			return price;
		}

		return __( 'Standard Equipment', 'at-vehicle-configurator' );
	};

	const mainImage = galleryImages[ selectedImageIndex ] || galleryImages[ 0 ] || null;
	let productTitle = __( 'Product', 'at-vehicle-configurator' );
	if ( productData ) {
		const decodedTitle = decodeHtmlEntities( productData.title?.rendered );
		productTitle = decodedTitle || __( 'Product', 'at-vehicle-configurator' );
	}
	const modalTitleId = `product-modal-title-${ productId }`;
	const modalDescriptionId = `product-modal-description-${ productId }`;

	return (
		<div
			className="configurator-product-info-modal-overlay"
			onClick={ handleOverlayClick }
			role="presentation"
		>
			<div
				ref={ modalRef }
				className="configurator-product-info-modal"
				role="dialog"
				aria-modal="true"
				aria-labelledby={ modalTitleId }
				aria-describedby={ modalDescriptionId }
				tabIndex={ -1 }
			>
				<div className="configurator-product-info-modal-header">
					<h2 id={ modalTitleId } className="configurator-product-info-modal-header-title">
						{ productData ? productTitle : __( 'Product', 'at-vehicle-configurator' ) }
					</h2>
					<button
						type="button"
						className="configurator-product-info-modal-close"
						onClick={ onClose }
						aria-label={ __( 'Close', 'at-vehicle-configurator' ) }
					>
						<Icon name="close" size="medium" />
					</button>
				</div>

				<div className="configurator-product-info-modal-body">
					{ loading && (
						<div className="configurator-product-info-modal-loading" role="status" aria-live="polite">
							<span className="screen-reader-text">{ __( 'Loading…', 'at-vehicle-configurator' ) }</span>
							<div className="configurator-product-info-modal-gallery" aria-hidden="true">
								<div className="configurator-product-info-modal-main-image">
									<span className="configurator-skeleton configurator-skeleton--image configurator-product-info-modal-main-image-skeleton" aria-hidden="true" />
								</div>
								<div className="configurator-product-info-modal-thumbnails">
									{ [ 1, 2, 3, 4 ].map( i => (
										<span key={ i } className="configurator-skeleton configurator-product-info-modal-thumbnail-skeleton" aria-hidden="true" />
									) ) }
								</div>
							</div>
							<div className="configurator-product-info-modal-content" aria-hidden="true">
								<div className="configurator-product-info-modal-status">
									<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '100px', height: '13px', display: 'block' } } />
								</div>
								<div className="configurator-product-info-modal-description">
									<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '85%', height: '16px', display: 'block', marginBottom: '12px' } } />
									<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '100%', height: '14px', display: 'block', marginBottom: '8px' } } />
									<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '95%', height: '14px', display: 'block', marginBottom: '8px' } } />
									<span className="configurator-skeleton configurator-skeleton--line" style={ { width: '70%', height: '14px', display: 'block', marginBottom: '24px' } } />
								</div>
								<div className="configurator-product-info-modal-actions">
									<span className="configurator-skeleton configurator-skeleton--button" style={ { width: '180px', marginRight: '8px' } } />
									<span className="configurator-skeleton configurator-skeleton--button" style={ { width: '80px' } } />
								</div>
							</div>
						</div>
					) }
					{ ! loading && productData && (
						<>
							{ mainImage && (
								<div className="configurator-product-info-modal-gallery">
									<div className="configurator-product-info-modal-main-image">
										<img
											src={ mainImage }
											alt={ getImageAltText( productData, productTitle ) }
										/>
									</div>
									{ galleryImages.length > 1 && (
										<div className="configurator-product-info-modal-thumbnails">
											{ galleryImages.map( ( imageUrl, index ) => {
												const thumbnailAlt = getImageAltText( productData, productTitle ) + ` ${ index + 1 }`;
												return (
													<button
														key={ index }
														type="button"
														className={ `configurator-product-info-modal-thumbnail ${ selectedImageIndex === index ? 'active' : ''
														}` }
														onClick={ () => setSelectedImageIndex( index ) }
														aria-label={ __( 'View image', 'at-vehicle-configurator' ) + ` ${ index + 1 } ${ __( 'of', 'at-vehicle-configurator' ) } ${ galleryImages.length }` }
														aria-current={ selectedImageIndex === index ? 'page' : undefined }
													>
														<img src={ imageUrl } alt={ thumbnailAlt } />
													</button>
												);
											} ) }
										</div>
									) }
								</div>
							) }

							<div className="configurator-product-info-modal-content">
								<div className="configurator-product-info-modal-status">
									{ getStatusText() }
								</div>

								{ ( () => {
									// Prefer short copy (REST short_description, excerpt, ACF short fields) over full description/content.
									const description =
										productData.short_description ||
										productData.excerpt?.rendered ||
										productData.acf?.short_description ||
										productData.acf?.short_descriptions ||
										productData.acf?.product_short_description ||
										productData.description ||
										productData.content?.rendered ||
										productData.acf?.description ||
										productData.acf?.content ||
										null;

									if ( ! description || description.trim() === '' ) {
										return (
											<div id={ modalDescriptionId } className="configurator-product-info-modal-description" aria-hidden="true">
												{ /* Empty description for ARIA */ }
											</div>
										);
									}

									return (
										<div id={ modalDescriptionId } className="configurator-product-info-modal-description">
											<div dangerouslySetInnerHTML={ { __html: description } } />
										</div>
									);
								} )() }

								<div className="configurator-product-info-modal-actions">
									{ isSelected ? (
										<button
											type="button"
											className="configurator-button configurator-button-secondary configurator-button-disabled"
											disabled
										>
											{ __( 'Included in configuration', 'at-vehicle-configurator' ) }
										</button>
									) : null }
									{ productData.link && ! productData.configurator_only && (
										<a
											href={ productData.link }
											className="configurator-button configurator-button-secondary"
											target="_blank"
											rel="noopener noreferrer"
										>
											{ __( 'View product page', 'at-vehicle-configurator' ) }
										</a>
									) }
									<button
										type="button"
										className="configurator-button configurator-button-secondary"
										onClick={ onClose }
									>
										{ __( 'Close', 'at-vehicle-configurator' ) }
									</button>
								</div>
							</div>
						</>
					) }
					{ ! loading && ! productData && (
						<div className="configurator-product-info-modal-error" role="alert" aria-live="assertive">
							{ __( 'Failed to load product information', 'at-vehicle-configurator' ) }
						</div>
					) }
				</div>
			</div>
		</div>
	);
};

export default ProductInfoModal;
